import { FC, useMemo } from 'react';
import { RsModalTrigger } from 'components/Rsuite/Modal';
import { RsButton, RsButtonSize } from 'components/Rsuite/Button';
import styles from './appHeaderNotificationBar.module.scss';

export enum LocalStorageKeys {
  NOTIFICATION_VERSION = 'notification-version',
}

const currentValue = {
  [LocalStorageKeys.NOTIFICATION_VERSION]: 'SINGLE_PAGE_DESIGN_V5',
};

interface MessageBlockProps {
  header: React.ReactNode;
  listItems: React.ReactNode[];
}

const MessageBlock: FC<MessageBlockProps> = ({ header, listItems }) => {
  return (
    <div className='flex flex-col gap-[6px]'>
      <h4 className='text-[20px]'>{header}</h4>
      <ul className='m-0 pl-[32px]'>
        {listItems.map((item: React.ReactNode, i: number) => (
          <li key={i} className='flex gap-[6px]'>
            •<div>{item}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

// const Bold = ({ children }) => (
//   <b className='text-[16px] font-extrabold'>{children}</b>
// );

const KeyWord: FC<{ children: React.ReactNode }> = ({ children }) => (
  <b className='font-bold text-[#5950B5]'>{children}</b>
);

export const AppHeaderNotificationBar: FC = () => {
  const handleConfirm = () => {
    const cookieExists = document.cookie
      .split('; ')
      .some((row) =>
        row.startsWith(`${LocalStorageKeys.NOTIFICATION_VERSION}=`),
      );

    if (!cookieExists) {
      const date = new Date();
      // Set cookie to expire in 1 day
      date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
      const expires = 'expires=' + date.toUTCString();
      document.cookie = `${
        LocalStorageKeys.NOTIFICATION_VERSION
      }=${currentValue[LocalStorageKeys.NOTIFICATION_VERSION]};${expires};path=/`;
    }
  };
  const defaultOpen = useMemo(() => {
    const cookieValue = document.cookie
      .split('; ')
      .find((row) =>
        row.startsWith(`${LocalStorageKeys.NOTIFICATION_VERSION}=`),
      )
      ?.split('=')[1];

    return cookieValue !== currentValue[LocalStorageKeys.NOTIFICATION_VERSION];
  }, []);

  return (
    <div className='flex h-full items-center bg-[#736AD7] px-[20px]'>
      <div className='flex-1 text-[16px] font-bold text-white'>
        <span>【重要平台公告】回饋金制度新上路</span>
      </div>
      <RsModalTrigger
        defaultOpen={defaultOpen}
        renderModalTrigger={(openModal) => (
          <RsButton
            size={RsButtonSize.EXTRA_SMALL}
            onClick={openModal}
            className='w-[115px] !font-bold !text-[#413993]'
          >
            了解詳情
          </RsButton>
        )}
        renderModalContent={(closeModal) => (
          <ModalContent
            onConfirm={() => {
              handleConfirm();
              closeModal();
            }}
          />
        )}
        modalProps={{
          backdrop: 'static',
        }}
      />
    </div>
  );
};

const ModalContent = ({ onConfirm }) => {
  return (
    <div className={styles['momo-modal-content'] + ' m-[12px]'}>
      <h2 className={styles['modal-content-header']}>
        【重要公告】2026年回饋金新制度調整，請詳閱
      </h2>
      <div className='my-[24px] flex flex-col gap-[25px] text-[15px]'>
        <div>
          <h3>回饋金使用新制度：</h3>
          <p className='mb-3'>
            為優化回饋金管理與帳戶使用體驗，將自 <KeyWord>2026年1月1日</KeyWord>
            起正式實施 <KeyWord>《回饋金使用規範》</KeyWord>(以下稱本規範)。
            本公告發布日期為 <KeyWord>2025年12月1日，並設有 30天宣告期</KeyWord>
            ，以利廣告主提前了解與調整回饋金使用安排。
          </p>

          <div>
            <h4>※ 生效時程與適用範圍</h4>
            <ul>
              <li>公告日期：2025年12月1日</li>
              <li>
                <KeyWord>正式生效日：2026年1月1日</KeyWord>
              </li>
            </ul>
          </div>
          <div className='mb-3'>
            <h4>※ 適用對象說明：</h4>
            <ol>
              <li>
                <KeyWord>
                  2026年1月1日（含）以後新發放之回饋金：將全面依據本規範執行。
                </KeyWord>
              </li>
              <li>
                <KeyWord>2025年12月31日（含）以前已歸戶之回饋金：</KeyWord>
                <ul className='mt-2'>
                  <li>
                    自2026年1月1日起計算
                    <KeyWord>4個月效期（至2026年4月30日止）。</KeyWord>
                  </li>
                  <li>
                    若至效期屆滿仍未使用完畢，
                    <KeyWord>剩餘金額將於2026年5月4日自帳戶中扣除</KeyWord>
                    ，不得要求補發、展延、折現或兌換其他服務。
                  </li>
                </ul>
              </li>
            </ol>
            <p className='underline underline-offset-4'>
              ＊
              請廣告主於2026年1月1日前留意帳戶中既有回饋金狀況，以避免影響使用權益。
            </p>
          </div>

          <div className='mb-3'>
            <h4>※ 重要提醒：</h4>
            <p>
              廣告主務必於2026年4月30日前使用完「2025年12月31日以前歸戶」的回饋金，以免因效期而導致自動扣除。
              詳細《回饋金使用規範》請見連結：
              <a
                href='https://bit.ly/4aaNZ5u'
                target='_blank'
                rel='noopener noreferrer'
              >
                https://bit.ly/4aaNZ5u
              </a>
            </p>
          </div>
        </div>
      </div>
      <div className='flex justify-end'>
        <RsButton
          appearance='primary'
          size={RsButtonSize.MEDIUM}
          onClick={onConfirm}
        >
          確定
        </RsButton>
      </div>
    </div>
  );
};
